import React, { useState, useEffect, useRef, useMemo } from 'react'
import { useHospitalSelection } from '../../hooks/useHospitalSelection'
import { usePersistedState } from '../../hooks/usePersistedState'
import LineChart from '../../components/Charts/LineChart'
import DateRangeSlider from '../../components/DateRangeSlider/DateRangeSlider'
import LoadingNotice from '../../components/LoadingNotice/LoadingNotice'
import { generatePDFIds } from '../../utils/pdfIdHelper'
import { parseMonthToValue } from '../../utils/dataUtils'
import { MonthInfo, generateMonthListFromObjects, initializeSliderValues, getSliderIndicesFromValues, TMB_SLIDER_KEY, extractActualMonthValuesFromObjects, checkHasDataForRange } from '../../utils/tmbUtils'
import { exportToPNG, exportToSVG, exportToCSV } from '../../utils/tmbExportUtils'
import { useTmbSlider } from '../../context/TmbSliderContext'
import '../../styles/shared/index.css'
import './PatientSurveys.css'

interface DataRow {
    hospital_id: string
    date: string
    goal_percentage: number | null
    patient_survey_count: number | null
    im_speaking_study_data: number | null
    hospital_data: number | null
}

const SERIES_COLORS = ['#0072B2', 'rgba(213, 149, 82, 0.6)', 'rgba(183, 175, 172, 0.6)']

const parseCount = (val: any): number | null => {
    if (val === null || val === '' || val === '""') return null
    const n = parseInt(val)
    return isNaN(n) ? null : n
}

const PatientSurveys: React.FC = () => {
    const { selectedHospitalIds, isMultiSelect, refreshKey } = useHospitalSelection()
    const { globalMonthList } = useTmbSlider()

    const [rawData, setRawData] = useState<DataRow[]>([])
    const [filteredData, setFilteredData] = useState<any[]>([])
    const [loading, setLoading] = useState(false)
    const [initialLoading, setInitialLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    const [monthList, setMonthList] = useState<MonthInfo[]>([])
    const [actualMonths, setActualMonths] = useState<Set<number>>(new Set())

    const persistenceKey = `${TMB_SLIDER_KEY}_${Array.isArray(selectedHospitalIds) ? selectedHospitalIds.join('_') : selectedHospitalIds}`
    const [sliderRange, setSliderRange] = usePersistedState<[number, number]>(persistenceKey, [0, 0])
    const [sliderStartIdx, sliderEndIdx] = getSliderIndicesFromValues(globalMonthList, sliderRange)
    const hasDataForRange = checkHasDataForRange(sliderRange[0], sliderRange[1], actualMonths)

    const [goal, setGoal] = useState<number>(30)

    useEffect(() => {
        if (!selectedHospitalIds || selectedHospitalIds.length === 0) {
            setRawData([])
            setFilteredData([])
            setLoading(false)
            setInitialLoading(false)
            setError(null)
            return
        }

        const hospitalIdsParam = Array.isArray(selectedHospitalIds)
            ? selectedHospitalIds.join(',')
            : selectedHospitalIds

        setLoading(true)
        setError(null)

        fetch(`/api/tmb/Patient_Survey_Transparency/${hospitalIdsParam}`)
            .then(res => {
                if (!res.ok) throw new Error('Failed to fetch data')
                return res.json()
            })
            .then(data => {
                const rows = data?.response?.result?.rows || []

                if (rows.length === 0) {
                    setError('No data available for selected hospital(s)')
                    setLoading(false)
                    setInitialLoading(false)
                    return
                }

                const parsed: DataRow[] = rows.map((row: any[]) => {
                    const goalRaw = parseInt(row[2])
                    return {
                        hospital_id: row[0],
                        date: row[1],
                        goal_percentage: (!isNaN(goalRaw) && goalRaw > 0) ? goalRaw : null,
                        patient_survey_count: parseCount(row[3]),
                        im_speaking_study_data: parseCount(row[4]),
                        hospital_data: parseCount(row[5]),
                    }
                })

                setRawData(parsed)
                setActualMonths(extractActualMonthValuesFromObjects(parsed, (row) => row.date))
                const months = generateMonthListFromObjects(parsed, (row) => row.date)
                setMonthList(months)

                if (months.length > 0) {
                    setSliderRange(prev => initializeSliderValues(months, prev, 12))
                }

                setLoading(false)
                setInitialLoading(false)
            })
            .catch(err => {
                setError(err.message || 'Failed to load data')
                setLoading(false)
                setInitialLoading(false)
            })
    }, [selectedHospitalIds, refreshKey])

    useEffect(() => {
        if (!rawData.length || !monthList.length) {
            setFilteredData([])
            return
        }

        const minMonthVal = sliderRange[0] || 0
        const maxMonthVal = sliderRange[1] || 0

        if (minMonthVal === 0 && maxMonthVal === 0) return

        const filtered = rawData.filter(row => {
            const val = parseMonthToValue(row.date)
            return val >= minMonthVal && val <= maxMonthVal
        })

        if (filtered.length === 0) {
            setFilteredData([])
            return
        }

        const hospitalIdsArr = Array.isArray(selectedHospitalIds) ? selectedHospitalIds : [selectedHospitalIds]
        if (hospitalIdsArr.length > 1) {
            const hospitalGoals: Record<string, number> = {}
            filtered.forEach(row => {
                const g = row.goal_percentage
                if (g !== null && g > 0) hospitalGoals[row.hospital_id] = g
            })
            const goalValues = Object.values(hospitalGoals)
            setGoal(goalValues.length > 0 ? Math.round(goalValues.reduce((a, b) => a + b, 0) / goalValues.length) : 30)
        } else {
            const firstWithGoal = filtered.find(r => r.goal_percentage !== null && r.goal_percentage > 0)
            setGoal(firstWithGoal?.goal_percentage ?? 30)
        }

        const monthMap = new Map<string, { total: number[], imSpeaking: number[], hospitalData: number[] }>()

        filtered.forEach(row => {
            const monthStr = row.date
            if (!monthMap.has(monthStr)) {
                monthMap.set(monthStr, { total: [], imSpeaking: [], hospitalData: [] })
            }
            const entry = monthMap.get(monthStr)!
            if (row.patient_survey_count !== null) entry.total.push(row.patient_survey_count)
            if (row.im_speaking_study_data !== null) entry.imSpeaking.push(row.im_speaking_study_data)
            if (row.hospital_data !== null) entry.hospitalData.push(row.hospital_data)
        })

        const sortedMonths = Array.from(monthMap.keys()).sort((a, b) =>
            parseMonthToValue(a) - parseMonthToValue(b)
        )

        const aggregate = (values: number[]) => {
            if (values.length === 0) return null
            return isMultiSelect ? values.reduce((sum, v) => sum + v, 0) : values[0]
        }

        const aggregated = sortedMonths.map(month => {
            const entry = monthMap.get(month)!
            const val = parseMonthToValue(month)
            const mm = String(val % 100).padStart(2, '0')
            const yyyy = Math.floor(val / 100)
            return {
                month: `${mm}/${yyyy}`,
                total: aggregate(entry.total),
                imSpeaking: aggregate(entry.imSpeaking),
                hospitalData: aggregate(entry.hospitalData),
            }
        })

        setFilteredData(aggregated)
    }, [rawData, monthList, sliderRange, isMultiSelect, selectedHospitalIds])

    const handleSliderChange = (startIndex: number, endIndex: number) => {
        setSliderRange([globalMonthList[startIndex]?.value || 0, globalMonthList[endIndex]?.value || 0])
    }

    const imSpeakingRecencyMonth = useMemo(() => {
        const relevant = rawData.filter(r => r.im_speaking_study_data !== null && r.im_speaking_study_data > 0)
        if (relevant.length === 0) return null
        const maxVal = Math.max(...relevant.map(r => parseMonthToValue(r.date)))
        if (maxVal === 0) return null
        const year = Math.floor(maxVal / 100)
        const month = maxVal % 100
        const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December']
        return `${MONTH_NAMES[month - 1]} ${year}`
    }, [rawData])

    const chartContainerRef = useRef<HTMLDivElement>(null)
    const [menuOpen, setMenuOpen] = useState(false)

    const handleDownloadPNG = async () => {
        if (!chartContainerRef.current) return
        await exportToPNG(chartContainerRef.current, 'Patient_Surveys', { title: 'Patient Surveys by Month' })
    }

    const handleDownloadSVG = async () => {
        if (!chartContainerRef.current) return
        await exportToSVG(chartContainerRef.current, 'Patient_Surveys', { title: 'Patient Surveys by Month' })
    }

    const handleDownloadCSV = () => {
        const rows = filteredData.map(d => [d.month, d.total ?? '', d.imSpeaking ?? '', d.hospitalData ?? ''])
        exportToCSV({
            headers: ['Category', 'Total', "I'm Speaking Study Data", 'Hospital Data'],
            rows,
            filename: 'Patient_Surveys',
        })
    }

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (!(e.target as Element).closest('.export-menu')) {
                setMenuOpen(false)
            }
        }
        document.addEventListener('click', handleClickOutside)
        return () => document.removeEventListener('click', handleClickOutside)
    }, [])

    if (initialLoading) {
        return <LoadingNotice title="Loading patient surveys data..." />
    }

    if (error) {
        return (
            <div className="patient-surveys-page tmb-page">
                <div className="error-container">
                    <p>Error: {error}</p>
                </div>
            </div>
        )
    }

    const hasData = filteredData.length > 0
    const categories = hasData ? filteredData.map(d => d.month) : []

    const totalData = hasData ? filteredData.map(d => d.total) : []
    const imSpeakingData = hasData ? filteredData.map(d => d.imSpeaking) : []
    const hospitalDataArr = hasData ? filteredData.map(d => d.hospitalData) : []

    const allValues = [...totalData, ...imSpeakingData, ...hospitalDataArr].filter(v => v !== null) as number[]
    const maxDataValue = allValues.length > 0 ? Math.max(...allValues, goal || 0) : 100
    const yAxisMax = maxDataValue > 100 ? Math.ceil(maxDataValue / 50) * 50 : 100
    const yAxisTickAmount = yAxisMax / 10

    return (
        <div id={generatePDFIds.dashboard()} className="patient-surveys-page tmb-page">
            <div className="page-header">
                <h1 className="page-title" style={{ margin: 0 }}>Patient Surveys by Month</h1>
                {globalMonthList.length > 0 && (
                    <div className="date-slider-container">
                        <DateRangeSlider
                            monthList={globalMonthList}
                            startIndex={sliderStartIdx}
                            endIndex={sliderEndIdx}
                            onChange={handleSliderChange}
                        />
                    </div>
                )}
            </div>

            <div id={generatePDFIds.chart(1)} className="chart-wrapper" ref={chartContainerRef} style={!hasData || !hasDataForRange ? { minHeight: 'auto' } : undefined}>
                {(!hasData || !hasDataForRange) ? (
                    <div style={{ padding: '14px 12px', fontSize: '13px', fontWeight: 600, color: '#000000' }}>
                        No data available
                    </div>
                ) : (<>
                <div className="export-menu-wrapper" style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.5rem' }}>
                    <div className="export-menu">
                        <button className="hamburger" onClick={(e) => { e.stopPropagation(); setMenuOpen(!menuOpen); }}>&#9776;</button>
                        <div className={`export-dropdown ${menuOpen ? 'show' : ''}`}>
                            <div onClick={async () => { await handleDownloadPNG(); setMenuOpen(false); }}>Download PNG</div>
                            <div onClick={async () => { await handleDownloadSVG(); setMenuOpen(false); }}>Download SVG</div>
                            <div onClick={() => { handleDownloadCSV(); setMenuOpen(false); }}>Download CSV</div>
                        </div>
                    </div>
                </div>
                <LineChart
                    categories={categories}
                    series={[
                        { name: 'Total', data: totalData },
                        { name: "I'm Speaking Study Data", data: imSpeakingData },
                        { name: 'Hospital Data', data: hospitalDataArr },
                    ]}
                    colors={SERIES_COLORS}
                    height={500}
                    goalLine={goal || 30}
                    goalLabel={`Min. 30% Goal: ${goal || 30}`}
                    goalLabelOffsetX={0}
                    yAxisLabel="Patient survey count"
                    xAxisLabel="Time Period"
                    min={0}
                    max={yAxisMax}
                    tickAmount={yAxisTickAmount}
                    isLoading={loading}
                    fontFamily="Poppins, Segoe UI, sans-serif"
                    showToolbar={true}
                    showLegend={true}
                    legendPosition="top"
                    legendAlign="center"
                    showDataLabels={true}
                    dataLabelsOffsetY={-8}
                    dataLabelsEnabledPerSeries={[true, false, false]}
                    dataLabelSuffix=""
                    decimals={0}
                    exportFilename="Patient Surveys"
                />
                {imSpeakingRecencyMonth && (
                    <p className="data-recency-footnote">
                        I'm Speaking study data updated through {imSpeakingRecencyMonth}
                    </p>
                )}
                </>)}
            </div>
        </div>
    )
}

export default PatientSurveys
